// @ts-ignore
import _ from 'lodash';
import {ref, computed, reactive} from "vue";
import {defineStore, storeToRefs} from "pinia";
import Event from "@/services/Event";
import {IRequestCar} from "@/interfaces/IRequestCar";
import {useCarStore} from "@/composables/cars";
import {useDriverStore} from "@/composables/drivers";
import {useCharacteristicsStore} from "@/composables/characteristics";
import * as RequestStatusesEnum from "@/emuns/request-status";

export const useEventStore = defineStore('events', () => {
    const eventService = new Event();

    // Объявляем массив событий
    const events = ref<IRequestCar[]>([]);

    // Пагинатор
    const paginate: {
        page: Number
        pages: Array<number>,
        totalPages: Number,
        count: Number,
    } = reactive({
        page: 1,
        pages: [],
        totalPages: 0,
        count: 0
    });

    // Фильтр
    const filters: {
        id: Number | null,
        status: String | null,
        page: Number
    } = reactive({
        id: null,
        status: 'wait',
        page: 0
    });

    // Объявляем текущее событие
    const event = ref<IRequestCar | null>(null);

    // Форма для заполнения
    const updateEventForm = reactive({
        car: {
            id: null,
            drivers: Array<number>(),
        },
        action: '',
        parameters: {},
        partner_confirmations: {},
    });

    const detachEventFormData = (): void => {
        updateEventForm.car = {
            id: null,
            drivers: Array<number>(),
        };

        updateEventForm.action = '';
        updateEventForm.parameters = {};
        updateEventForm.partner_confirmations = {};
    };

    const clearEvents = () => {
        events.value = [];
    };

    const characteristicsStore = useCharacteristicsStore();
    const {characteristics} = storeToRefs(characteristicsStore);

    const carStore = useCarStore();
    const {cars} = storeToRefs(carStore);

    const driverStore = useDriverStore();

    // Функция для получения списка событий
    const loadEvents = async () => {
        clearEvents();

        const {data} = await eventService.get(Object.assign(filters, {
            _p: filters.page
        }));

        const {footer_data} = data;
        const {page, pages, total_pages, row_count} = footer_data;

        // Обновляем пагинатор
        paginate.page = page;
        paginate.pages = pages;
        paginate.totalPages = total_pages;
        paginate.count = row_count;

        // Обновляем события
        const preventEvents: IRequestCar[] = [];
        data.rows.forEach((e: any) => {
            const event: IRequestCar = {
                id: e.request_car_id,
                carId: e.car_id,
                requestCarId: e.request_car_id,
                requestId: e.requestId,
                orderId: e.orderId,
                status: e.status,
                date: e.request_date,
                carTypeName: e.car_type_name,
                car: null,
                technics: e.technics,
                collapseData: {},
                partnerAgreeDate: e.partner_agree_date,
                company: e.companies_name,
                departments: e.departments,
            };

            preventEvents.push(event);
        });
        events.value = preventEvents;
    };

    // Поиск конкретного event
    const loadEvent = async (id: number) => {
        // Предварительно чистим форму
        detachEventFormData();

        const {data: {data}} = await eventService.find(id);

        const row = data.rows.length ? data.rows[0] : null;
        if (!row) {
            throw new Error('Запрос не найден');
        }

        // Сохраняем настройки характеристик
        characteristicsStore.updateCharacteristics(
            data.characteristics_names
        );

        event.value = {
            id: row.request_id,
            requestId: row.request_id,
            requestCarId: row.request_car_id,
            orderId: row.orderId,
            status: row.status,
            date: row.request_date,
            carTypeName: row.car_type_name,
            carId: row.car_id,
            car: null,
            technics: _.clone(row.technics),
            collapseData: row.collapse_data,
            partnerAgreeDate: row.partner_agree_date,
            company: row.companies_name,
            departments: row.departments
        };

        if (event.value.technics.length) {
            // @ts-ignore
            const [technic] = event.value.technics;
            // @ts-ignore
            Object.keys(event.value.collapseData).forEach(key => {
                // @ts-ignore
                if (!updateEventForm.parameters) {
                    // @ts-ignore
                    updateEventForm.parameters = {}
                }

                // @ts-ignore
                updateEventForm.parameters[key] = technic.filled_parameters.parameters ? technic.filled_parameters.parameters[key] : '';
            })
            // @ts-ignore
        }

        updateEventForm.car.id = row.technics.length ? row.technics[0].car_id : null;

        if (updateEventForm.car.id) {
            updateEventForm.car.drivers = row.technics[0].drivers.map((driver: any) => driver.driver_id);
        }

        const {cars, drivers} = data;

        // Обновляем список машин
        carStore.updateCars(cars);

        // Обновляем список водителей
        driverStore.updateDrivers(drivers)
    }

    const eventCar = computed(() => {
        return updateEventForm.car.id
            // @ts-ignore
            ? cars.value.find(c => {
                // @ts-ignore
                return c.id === updateEventForm.car.id
            })
            : null
    });

    const getStatusRequest = (status: string): string => {
        switch (status) {
            case RequestStatusesEnum.WAIT_STATUS:
                return 'Ожидает';
            case RequestStatusesEnum.AGREE_STATUS:
                return 'Утверждено';
            case RequestStatusesEnum.DISAGREE_STATUS:
                return 'Отклонено';
            default:
                return ''
        }
    };

    // @ts-ignore
    const updateDriversSelected = ({status, id}) => {
        if (!status) {
            updateEventForm.car.drivers = updateEventForm.car.drivers.filter(driver => driver !== id);
            return;
        }

        updateEventForm.car.drivers = [id];
    };

    const saveHandle = async (action: string) => {
        if (event.value === null) return;

        const saveData = {
            action,
            partner_confirmation: {
                // @ts-ignore
                [event.value.requestCarId]: action
            },
            cars: {
                // @ts-ignore
                [event.value.requestCarId]: [{
                    portal_id: updateEventForm.car.id,
                    drivers: updateEventForm.car.drivers.map(driver => ({
                        portal_id: driver
                    }))
                }]
            },
            parameters: {
                // @ts-ignore
                [event.value.requestCarId]: updateEventForm.parameters
            },
        }

        // @ts-ignore
        eventService.save(event.value.id, saveData);
    };

    return {
        event,
        events,
        filters,
        paginate,
        eventCar,
        loadEvent,
        loadEvents,
        saveHandle,
        getStatusRequest,
        updateEventForm,
        updateDriversSelected
    }
});